import FilterByMenu from 'components/FilterByMenu';
import DateTimeSelect from 'components/Form/DateTimeSelect';
import Dropdown from 'components/Form/Dropdown';
import InputText from 'components/Form/InputText';
import SubmitButton from 'components/Form/SubmitButton';
import Icon from 'components/Icon';
import Layout from 'components/Layout';
import LinkButton from 'components/LinkButton';
import {FC} from 'react';
import {useFormContext} from 'react-hook-form';
import {useBackend} from 'state/backend';
import {t} from 'ttag';
import {Option} from 'types';
import {jstDateString} from 'utils/date';
import {toCamelCase} from 'utils/object';
import {setUrl} from 'utils/request';

import {DocumentArchiveBackendData, FilterFormData} from '../../types';

export type AutocompleteSearchItem = {
    clientExternalId: string;
    id: string;
    itemType: ItemType;
    name: string;
};
export type AutocompleteSearchResult = {
    hasNext: boolean;
    items: AutocompleteSearchItem[];
};

type ItemType = 'client' | 'document';

const parseSearchResponse = (item: AutocompleteSearchItem): Option => {
    const camelCaseItem = toCamelCase(item);

    return {
        label: (
            <div className="flex flex-row items-center gap-x-2">
                <Icon
                    icon={
                        camelCaseItem.itemType === 'client'
                            ? 'user'
                            : 'document'
                    }
                    size="base"
                />
                {camelCaseItem.name}
            </div>
        ),
        value: `${camelCaseItem.itemType}_${camelCaseItem.id}`,
    };
};

export type AutocompleteEmployeeItem = {
    mid: string;
    name: string;
};

const Filter: FC = () => {
    const {
        data: {
            deliveryMethods,
            documentTypes,
            initialSearchOptions,
            isFiltered,
            legalDeliveryCategories,
            pagination,
            sentByList,
        },
    } = useBackend<DocumentArchiveBackendData>();

    const {handleSubmit} = useFormContext<FilterFormData>();

    const handleFilter = (formData: FilterFormData) => {
        const builtUrl = new URL(window.location.href.split('?')[0]);

        const queryParameters = new URLSearchParams(builtUrl.search);

        Object.entries(formData).forEach(([key, value]) => {
            if (value) {
                if (
                    key === 'date_issued_begin' ||
                    key === 'date_issued_end' ||
                    key === 'date_sent_begin' ||
                    key === 'date_sent_end'
                ) {
                    queryParameters.append(key, jstDateString(value));
                } else if (key === 'search') {
                    const [itemType, search] = value.split('_');
                    queryParameters.append('item_type', itemType);
                    queryParameters.append('search', search);
                } else {
                    queryParameters.append(key, value);
                }
            }
        });

        builtUrl.search = queryParameters.toString();

        setUrl(builtUrl.toString());
    };

    return (
        <form onSubmit={handleSubmit(handleFilter)}>
            <Layout.Chain className="mt-4">
                <Dropdown
                    className="w-1/4"
                    isCompact={true}
                    isSearchable={true}
                    name="search"
                    placeholder={t`Search`}
                    serverRequest={{
                        fetcher: (query) =>
                            `${window.Urls.preservation_autocomplete_view()}${query ? '?search=' + query : ''}&page=${pagination.currentPage}`,
                        initialSelectedOptions:
                            initialSearchOptions.map(parseSearchResponse),
                        parser: (data: AutocompleteSearchResult) =>
                            data.items.map(parseSearchResponse),
                    }}
                    type="select"
                />

                <Dropdown
                    className="w-1/5"
                    isCompact={true}
                    isSearchable={true}
                    name="document_type"
                    options={documentTypes}
                    placeholder={t`All Document Types`}
                    type="select"
                />
                <Dropdown
                    className="w-1/5"
                    isCompact={true}
                    name="delivery_methods"
                    options={deliveryMethods}
                    placeholder={t`All Delivery Methods`}
                    type="select"
                />
                <Dropdown
                    className="w-1/5"
                    isCompact={true}
                    name="legal_categories"
                    options={legalDeliveryCategories}
                    placeholder={t`All Legal Delivery Categories`}
                    type="select"
                />
                <FilterByMenu>
                    <Layout.Stacked className="mb-8" isDoubleStacked={true}>
                        <DateTimeSelect
                            isCompact={true}
                            label={t`Dated after`}
                            name="date_issued_begin"
                        />
                        <DateTimeSelect
                            isCompact={true}
                            label={t`Dated before`}
                            name="date_issued_end"
                        />
                        <DateTimeSelect
                            isCompact={true}
                            label={t`Archived after`}
                            name="date_sent_begin"
                        />
                        <DateTimeSelect
                            isCompact={true}
                            label={t`Archived before`}
                            name="date_sent_end"
                        />
                        <InputText
                            isCompact={true}
                            label={t`Total more than`}
                            name="amount_begin"
                        />
                        <InputText
                            isCompact={true}
                            label={t`Total less than`}
                            name="amount_end"
                        />
                    </Layout.Stacked>
                    <Layout.Stacked className="mb-4">
                        <InputText
                            isCompact={true}
                            label={t`Document External ID`}
                            name="external_id"
                        ></InputText>
                        <Dropdown
                            isCompact={true}
                            label={t`Sent by`}
                            name="employee"
                            options={sentByList}
                            placeholder={t`<No Team Member>`}
                            type="input"
                        />
                    </Layout.Stacked>
                </FilterByMenu>

                <SubmitButton
                    className="min-w-10"
                    icon="search"
                    kind="secondary"
                    size="sm"
                    title={t`Filter`}
                />
                {isFiltered && (
                    <LinkButton
                        className="min-w-10"
                        href={window.Urls.preservation_list_view()}
                        icon="close"
                        kind="secondary"
                        size="sm"
                        title={t`Clear`}
                    />
                )}
            </Layout.Chain>
        </form>
    );
};

export default Filter;
